import { environment } from '../../environments/environment';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Decisioning } from '@app/app/interfaces/decisioning.model';
import { Observable } from 'rxjs';
import { BorrowerAttribute } from '@app/app/interfaces/borrower-attribute.model';
import { DecisioningBatch } from "@app/app/interfaces/decisioning-batch.model";
import { Qualifier, QualifierValue, ResultsByQualifier } from '@app/app/interfaces/qualifier.model';
import { format, parseISO, differenceInHours } from 'date-fns';
import * as _ from 'lodash';
import { AuthState } from '../store/auth/auth.state';
import { Store } from '@ngxs/store';

export interface AttributesMap {
  [alias: string]: BorrowerAttribute
}

export interface DecisioningResponse {
  data: {
    decisioning: Decisioning[];
    attributes: AttributesMap
  }
}

export interface DecisioningBatchesResponse {
  data: {
    decisioningBatches: DecisioningBatch[];
  }
}

@Injectable( {
  providedIn: 'root',
} )
export class DecisioningService {

  constructor(
    private httpClient: HttpClient,
    private store: Store,
  ) {
  }

  getBorrowerDecisioningForProduct( borrowerId: number, loanProductId: number ): Observable<DecisioningResponse> {
    return this.httpClient.get<DecisioningResponse>(
      `${ environment.apiUrl }/lender-portal/borrowers/${borrowerId}/decisioning-matches?loanProductIds[]=${loanProductId}`
    )
  }

  getLenderDecisioningBatches( lenderId: number ): Observable<DecisioningBatchesResponse> {
    return this.httpClient.get<DecisioningBatchesResponse>( `${ environment.apiUrl }/lender-portal/decisioning-batches`)
  }

  getFormattedQualifierValue( qualifier: Qualifier, resultData: ResultsByQualifier, attributesMap: AttributesMap ): QualifierValue {
    return {
      qualifierLabel: attributesMap[qualifier.field]?.name,
      requirementLabel: this.getRequirementLabel( qualifier, attributesMap[qualifier.field] ),
      actualValue: this.formatQualifierValue( qualifier, resultData, attributesMap[qualifier.field] ),
      status: resultData.matchValue,
      sourcesMatch: resultData.usedSource === resultData.qualifierAllowedSource,
      fromVerifiedSource: this.isVerifiedSource( qualifier ),
      verifiedSourceLabel: this.getVerifiedSourceLabel( qualifier ),
      verifiedSourceDate: this.getVerifiedSourceDate( qualifier, resultData ),
    }
  }

  formatQualifierValue( qualifier: Qualifier, resultData: ResultsByQualifier, attribute: any ): string {
    if (qualifier.field === 'computed.prohibited_industry_lists' || qualifier.field === 'computed.high_risk_industry_lists') {
      return resultData.matchValue === 'accepted' ? 'No' : 'Yes';
    }
    if (qualifier.field === 'computed.bankruptcyDischargeYears') {
      return resultData.usedValue == '999' ? 'No Bankruptcy'
              : (resultData.usedValue == '-1' ? 'Current Bankruptcy'
                : resultData.usedValue
              );
    } 
    if ( resultData.usedValue === null || (resultData.qualifierAllowedSource && resultData.usedSource !== resultData.qualifierAllowedSource) ) {
      return 'Missing';
    }
    if ( qualifier.filterType === 'is' ) {
      return resultData.usedValue === '1' ? 'Yes' : 'No';
    }
    if (attribute.type === 'currency') {
      return this.formatNumber( parseFloat(resultData.usedValue), 'currency' );
    }
    if (attribute.type === 'number') {
      return (parseFloat(resultData.usedValue) - Math.floor(parseFloat(resultData.usedValue)) !== 0)
      ? parseFloat(resultData.usedValue).toFixed(2).toString()
      : parseFloat(resultData.usedValue).toString()
    }
    return resultData.usedValue;
  }

  getRequirementLabel( qualifier: Qualifier, attribute: any ): string {
    if (qualifier.field === 'computed.prohibited_industry_lists' || qualifier.field === 'computed.high_risk_industry_lists') {
      return `Must be 'No'`;
    } else {
      let readable, min, max;
      switch ( qualifier.filterType ) {
        case 'contains':
          readable = this.joinWithSeparator( qualifier.contains, ', ', 'or ' );
          return `Must have one (or more) of ${ readable }`;

        case 'notContains':
          readable = this.joinWithSeparator( qualifier.notContains, ', ', 'or ' );
          return `Must have none of ${ readable }`;

        case 'in':
          readable = this.joinWithSeparator( qualifier.in, ', ', 'or ' );
          return `Must be ${ readable }`;

        case 'notIn':
          readable = this.joinWithSeparator( qualifier.notIn, ', ', 'or ' );
          return `Cannot be ${ readable }`;

        case 'is':
          if(attribute.type === 'bool' || attribute.type === 'boolean') {
            return `Must be '${ qualifier.is === '1' ? 'Yes' : 'No' }'`;
          }
          return `Must be ${qualifier.is}`;

        case 'range':
          min = this.formatNumber( qualifier.min, attribute.type );
          max = this.formatNumber( qualifier.max, attribute.type );
          return `Must be between ${ min } and ${ max }`;

        case 'min':
          min = this.formatNumber( qualifier.min, attribute.type );
          return `Must be at least ${ min }`;

        case 'max':
          max = this.formatNumber( qualifier.max, attribute.type );
          return `Must be ${ max } or less`;

        default:
          return '(INVALID FILTER)';
      }
    }
  }

  joinWithSeparator( list: string[], separator: string, term: string ): string {
    if ( !Array.isArray( list ) || list.length === 0 ) {
      return 'INVALID FILTER';
    }

    if ( list.length === 1 ) {
      return list[0];
    }

    if ( list.length === 2 ) {
      return `${ list[0] } ${ term } ${ list[1] }`;
    }

    const last = list.pop();
    return `${ list.join( separator ) }${ separator }${ term }${ last }`;
  }

  formatNumber( number: number, type: string ): string {
    return ( type === 'currency' ? '$' : '' ) + this.addCommas( number );
  }

  addCommas( number: number ): string {
    const parts = (number * 1).toString().split( '.' );
    parts[0] = parts[0].replace( /\B(?=(\d{3})+(?!\d))/g, ',' );
    return parts.join( '.' );
  }

  isVerifiedSource( qualifier: Qualifier ): boolean {
    const verifiedSourceIds = [
      10, // transunion
      11, // lexisnexis
      12, // international_trade_administration
      3, // experian_consumer
      4, // ocrolus
      7, // experian_commercial
      8, // dun_and_bradstreet
      13, // finicity
    ];
    return verifiedSourceIds.includes(qualifier.sourceId || 0);
  }

  getVerifiedSourceLabel( qualifier: Qualifier ): string {
    let verifiedLabel = '';
    if(this.isVerifiedSource(qualifier)){
      if(qualifier.sourceId){
        switch(qualifier.sourceId){
          case 10:
            verifiedLabel = 'TransUnion';
            break;
          case 11:
            verifiedLabel = 'LexisNexis';
            break;
          case 12:
            verifiedLabel = 'International Trade Administration (ITA)';
            break;
          case 3:
          case 7:
            verifiedLabel = 'Experian';
            break;
          case 4:
            verifiedLabel = 'Ocrolus OCR';
            break;
          case 8:
            verifiedLabel = 'Dun and Bradstreet';
            break;
          case 13:
            verifiedLabel = 'Finicity';
            break;
          default:
            return 'N/A';
        }
      }
    } else {
      verifiedLabel = 'N/A';
    }
    return verifiedLabel;
  }

  getVerifiedSourceDate( qualifier: Qualifier, result: ResultsByQualifier ): string {
    let verifiedDate = '';
    if(this.isVerifiedSource(qualifier) && result.modified){
      if(qualifier.sourceId){
        const dateObject = parseISO(result.modified);
        const qualifierModifiedDate = format(dateObject, 'MM/dd/yyyy');
        verifiedDate = qualifierModifiedDate;
      }
    } else {
      verifiedDate = 'N/A';
    }
    return verifiedDate;
  }

  shouldShowDecisioningSnackbar(decisioningProgress: DecisioningBatch[]) {
    const decisioningProgressForSort = [...decisioningProgress];
    const mostRecentDecisioningBatch = decisioningProgressForSort.sort(({id:a}, {id:b}) => b-a)[0];
    if (mostRecentDecisioningBatch){
      const mostRecentBatchDate = new Date(mostRecentDecisioningBatch.created);
      const isWithinLast24Hours = differenceInHours(new Date(), mostRecentBatchDate) <= 24;
      const dismissDecisioningSnackbarList = JSON.parse(localStorage.getItem('DismissDecisioningSnackbarList') || '[]');
      return mostRecentDecisioningBatch.total > 0 &&
              mostRecentDecisioningBatch.importId !== null &&
              mostRecentDecisioningBatch.lenderId == this.store.selectSnapshot(AuthState.user)?.institution?.id &&
              isWithinLast24Hours &&
              !dismissDecisioningSnackbarList?.includes(mostRecentDecisioningBatch.id);
    } else {
      return false;
    }
  }
}
